import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { UserMonitorConfig } from '../../../types/UserMonitorConfig';
import type { Monitor, ScalingPreset } from '../../../types/Monitor';
import { calculatePpi } from '../../../utils/ppiCalculator';
import {
  getPresetsForMonitor,
  getGenericScalingPresets,
  getDefaultPreset,
} from '../../../utils/scalingMode';
import { getAllMonitors } from '../../../services/MonitorService';
import styles from './UserMonitorSetup.module.css';

interface UserMonitorSetupProps {
  /** Current saved config */
  config: UserMonitorConfig;
  /** Called when the user saves a valid config */
  onSave: (config: UserMonitorConfig) => void;
  /** Optional: called when the user dismisses without saving */
  onCancel?: () => void;
}

const PPI_HIDPI_THRESHOLD = 150;

const CUSTOM_LABEL = 'Custom / not listed';

/** Returns the display label for a catalogue monitor */
function monitorLabel(m: Monitor): string {
  return `${m.brand} ${m.name} — ${m.diagonalInches}" ${m.nativeResolutionW}×${m.nativeResolutionH}`;
}

export function UserMonitorSetup({ config, onSave, onCancel }: UserMonitorSetupProps) {
  const [catalogue, setCatalogue] = useState<Monitor[]>([]);
  const [selectedId, setSelectedId] = useState<string>('custom');

  // Search-as-you-type state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef   = useRef<HTMLInputElement>(null);

  const [diagonal, setDiagonal] = useState<string>(String(config.diagonalInches));
  const [resW, setResW] = useState<string>(String(config.nativeResolutionW));
  const [resH, setResH] = useState<string>(String(config.nativeResolutionH));
  const [selectedPreset, setSelectedPreset] = useState<ScalingPreset | null>(null);

  // Load the catalogue once
  useEffect(() => {
    getAllMonitors().then((monitors) => {
      setCatalogue(monitors);
      // Try to pre-select the monitor matching the saved config
      const match = monitors.find(
        (m) =>
          m.nativeResolutionW === config.nativeResolutionW &&
          m.nativeResolutionH === config.nativeResolutionH &&
          m.diagonalInches === config.diagonalInches
      );
      if (match) {
        setSelectedId(match.id);
        setSearchQuery(monitorLabel(match));
        // Restore the saved preset by matching logical resolution
        const presets = getPresetsForMonitor(match);
        const saved = presets.find(
          (p) => p.logicalW === config.scalingLogicalW && p.logicalH === config.scalingLogicalH
        ) ?? getDefaultPreset(presets);
        setSelectedPreset(saved);
      }
    });
  }, [config]);

  // Close dropdown when clicking outside the search widget
  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setIsSearchOpen(false);
        // Restore display label if a monitor is still selected
        if (selectedId !== 'custom') {
          const m = catalogue.find((x) => x.id === selectedId);
          if (m) setSearchQuery(monitorLabel(m));
        }
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [selectedId, catalogue]);

  // Filtered catalogue based on current search query
  const filteredMonitors = useMemo<Monitor[]>(() => {
    const q = searchQuery.toLowerCase().trim();
    // If the query exactly matches the already-selected monitor's label, show all
    if (selectedId !== 'custom') {
      const m = catalogue.find((x) => x.id === selectedId);
      if (m && monitorLabel(m).toLowerCase() === q) return catalogue;
    }
    if (!q) return catalogue;
    return catalogue.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.brand.toLowerCase().includes(q) ||
        String(m.diagonalInches).includes(q) ||
        `${m.nativeResolutionW}x${m.nativeResolutionH}`.includes(q) ||
        `${m.nativeResolutionW}×${m.nativeResolutionH}`.includes(q)
    );
  }, [catalogue, searchQuery, selectedId]);

  // Derived values
  const diagNum = parseFloat(diagonal);
  const wNum    = parseInt(resW, 10);
  const hNum    = parseInt(resH, 10);
  const isValid =
    !isNaN(diagNum) && diagNum > 5 && diagNum < 120 &&
    !isNaN(wNum) && wNum >= 640 &&
    !isNaN(hNum) && hNum >= 480;
  const livePpi = isValid ? calculatePpi(wNum, hNum, diagNum) : null;

  /** HiDPI scaling is only meaningful on high-density panels */
  const showScaling = livePpi !== null && livePpi >= PPI_HIDPI_THRESHOLD;

  // Compute available scaling presets from the selected catalogue monitor
  // (for custom/unknown monitors fall back to generic).
  const availablePresets = useMemo<ScalingPreset[]>(() => {
    if (!isValid) return [];
    if (selectedId !== 'custom') {
      const m = catalogue.find((x) => x.id === selectedId);
      if (m) return getPresetsForMonitor(m);
    }
    return getGenericScalingPresets(wNum, hNum);
  }, [selectedId, catalogue, isValid, wNum, hNum]);

  // Auto-select default preset when presets change (monitor changed)
  const prevSelectedIdRef = useRef(selectedId);
  useEffect(() => {
    if (selectedId !== prevSelectedIdRef.current && availablePresets.length > 0) {
      setSelectedPreset(getDefaultPreset(availablePresets));
    }
    prevSelectedIdRef.current = selectedId;
  }, [selectedId, availablePresets]);

  // Select a monitor from the search dropdown
  const handleSelectMonitor = useCallback((m: Monitor) => {
    setSelectedId(m.id);
    setSearchQuery(monitorLabel(m));
    setIsSearchOpen(false);
    setDiagonal(String(m.diagonalInches));
    setResW(String(m.nativeResolutionW));
    setResH(String(m.nativeResolutionH));
    // Preset is reset by the prevSelectedIdRef effect above
  }, []);

  // Select "Custom / not listed"
  const handleSelectCustom = useCallback(() => {
    setSelectedId('custom');
    setSearchQuery('');
    setIsSearchOpen(false);
    setSelectedPreset(null);
  }, []);

  // When any field is manually edited, switch to custom
  function handleDiagonalChange(val: string) {
    setDiagonal(val);
    setSelectedId('custom');
    setSearchQuery('');
  }
  function handleWChange(val: string) {
    setResW(val);
    setSelectedId('custom');
    setSelectedPreset(null);
    setSearchQuery('');
  }
  function handleHChange(val: string) {
    setResH(val);
    setSelectedId('custom');
    setSelectedPreset(null);
    setSearchQuery('');
  }

  // Ensure a valid preset is selected once availablePresets are ready
  const effectivePreset = selectedPreset ?? (availablePresets.length > 0 ? getDefaultPreset(availablePresets) : null);

  function handleSave() {
    if (!isValid) return;
    // Low-PPI monitors: save 1:1 (logical = native, no HiDPI scaling)
    if (!showScaling) {
      onSave({
        diagonalInches: diagNum,
        nativeResolutionW: wNum,
        nativeResolutionH: hNum,
        scalingLogicalW: wNum,
        scalingLogicalH: hNum,
      });
      return;
    }
    if (!effectivePreset) return;
    onSave({
      diagonalInches: diagNum,
      nativeResolutionW: wNum,
      nativeResolutionH: hNum,
      scalingLogicalW: effectivePreset.logicalW,
      scalingLogicalH: effectivePreset.logicalH,
    });
  }

  const isSaveDisabled = !isValid || (showScaling && !effectivePreset);

  return (
    <div className={styles.form}>
      <div className={styles.header}>
        <h2 className={styles.title}>Your Monitor</h2>
        <p className={styles.subtitle}>
          Enter your current screen's specs so the preview can scale accurately.
        </p>
      </div>

      <div className={styles.fields}>
        {/* ── Search catalogue ── */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="umc-search">
            Search catalogue
          </label>
          <div className={styles.searchWrapper} ref={searchWrapperRef}>
            <input
              ref={searchInputRef}
              id="umc-search"
              className={styles.input}
              type="text"
              autoComplete="off"
              value={searchQuery}
              placeholder={CUSTOM_LABEL}
              onFocus={() => {
                // Clear the label so the user starts with a clean query
                if (selectedId !== 'custom') {
                  const m = catalogue.find((x) => x.id === selectedId);
                  if (m && searchQuery === monitorLabel(m)) setSearchQuery('');
                }
                setIsSearchOpen(true);
              }}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsSearchOpen(true);
              }}
              aria-autocomplete="list"
              aria-controls="umc-search-listbox"
              aria-expanded={isSearchOpen}
              role="combobox"
            />
            {isSearchOpen && (
              <ul
                id="umc-search-listbox"
                className={styles.searchDropdown}
                role="listbox"
              >
                <li
                  className={`${styles.searchOption} ${styles.searchOptionCustom} ${selectedId === 'custom' ? styles.searchOptionSelected : ''}`}
                  role="option"
                  aria-selected={selectedId === 'custom'}
                  onMouseDown={(e) => { e.preventDefault(); handleSelectCustom(); }}
                >
                  {CUSTOM_LABEL}
                </li>
                {filteredMonitors.length === 0 ? (
                  <li className={styles.searchNoResults}>No monitors found</li>
                ) : (
                  filteredMonitors.map((m) => (
                    <li
                      key={m.id}
                      className={`${styles.searchOption} ${m.id === selectedId ? styles.searchOptionSelected : ''}`}
                      role="option"
                      aria-selected={m.id === selectedId}
                      onMouseDown={(e) => { e.preventDefault(); handleSelectMonitor(m); }}
                    >
                      <span className={styles.searchOptionName}>{m.brand} {m.name}</span>
                      <span className={styles.searchOptionMeta}>
                        {m.diagonalInches}&quot; &middot; {m.nativeResolutionW}&times;{m.nativeResolutionH} &middot; {m.ppi.toFixed(1)} ppi
                      </span>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </div>

        {/* ── Diagonal ── */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="umc-diagonal">
            Screen Size
          </label>
          <div className={styles.inputWithUnit}>
            <input
              id="umc-diagonal"
              className={styles.input}
              type="number"
              min={5}
              max={120}
              step={0.1}
              value={diagonal}
              onChange={(e) => handleDiagonalChange(e.target.value)}
              placeholder="27"
              aria-describedby="umc-diagonal-unit"
            />
            <span className={styles.unit} id="umc-diagonal-unit">inches</span>
          </div>
        </div>

        {/* ── Resolution ── */}
        <div className={styles.fieldGroup}>
          <span className={styles.label}>Native Resolution</span>
          <div className={styles.resolutionRow}>
            <input
              className={styles.input}
              type="number"
              min={640}
              max={15360}
              step={1}
              value={resW}
              onChange={(e) => handleWChange(e.target.value)}
              aria-label="Horizontal resolution"
              placeholder="2560"
            />
            <span className={styles.resSep}>&times;</span>
            <input
              className={styles.input}
              type="number"
              min={480}
              max={8640}
              step={1}
              value={resH}
              onChange={(e) => handleHChange(e.target.value)}
              aria-label="Vertical resolution"
              placeholder="1440"
            />
            <span className={styles.unit}>px</span>
          </div>
        </div>

        {/* ── Scaling preset — only shown for HiDPI (≥150 ppi) panels ── */}
        {showScaling && (
        <div className={styles.fieldGroup}>
          <span className={styles.label}>Scaling Mode</span>
          {availablePresets.length > 0 ? (
            <div className={styles.segmented} role="group" aria-label="Scaling mode">
              {availablePresets.map((preset) => {
                const isActive =
                  effectivePreset?.logicalW === preset.logicalW &&
                  effectivePreset?.logicalH === preset.logicalH;
                return (
                  <button
                    key={`${preset.logicalW}x${preset.logicalH}`}
                    type="button"
                    className={`${styles.segBtn} ${isActive ? styles.segBtnActive : ''}`}
                    onClick={() => setSelectedPreset(preset)}
                    aria-pressed={isActive}
                  >
                    <span className={styles.segBtnLabel}>
                      {preset.label || `${preset.logicalW}×${preset.logicalH}`}
                    </span>
                    <span className={styles.segBtnRes}>
                      looks like {preset.logicalW}×{preset.logicalH}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className={styles.hint}>Enter a valid resolution above to see scaling options.</p>
          )}
          <p className={styles.hint}>
            Match what you see in System Settings &rarr; Displays.
          </p>
        </div>
        )}

        {/* ── Live PPI readout ── */}
        {livePpi !== null && (
          <div className={styles.ppiReadout}>
            <span className={styles.ppiLabel}>Calculated PPI</span>
            <span className={styles.ppiValue}>{livePpi.toFixed(1)}</span>
          </div>
        )}
      </div>

      {/* ── Actions ── */}
      <div className={styles.actions}>
        {onCancel && (
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={isSaveDisabled}
          aria-disabled={isSaveDisabled}
        >
          Save
        </button>
      </div>
    </div>
  );
}
